<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Vendor bills</h1>
            <p class="text-muted mb-0">
              Manage your vendor bills and track payments
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create bill
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Desktop View: Tabs -->
    <div class="d-none d-lg-block">
      <CTabs :active-tab="0" variant="tabs" add-tab-classes="mb-3">
        <!-- Tab 1: Analytics -->
        <CTab>
          <template slot="title">
            <CIcon name="cil-chart-line" class="mr-1" /> Analytics
          </template>

          <!-- Bill Insights -->
          <CRow class="mb-4">
            <!-- Bill Summary -->
            <CCol lg="8">
        <CCard class="h-100 border-0 shadow-sm">
          <CCardHeader
            class="bg-transparent border-0 d-flex justify-content-between align-items-center p-4"
          >
            <h5 class="mb-0">Bill summary</h5>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <CRow class="mb-4">
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Total bills</span>
                    <span class="inv-kpi-chip chip-navy"><CIcon name="cil-file" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ billSummary.totalCount }}</div>
                  <div class="inv-kpi-sub" :class="billSummary.countChange >= 0 ? 'trend-up' : 'trend-down'">
                    <CIcon :name="billSummary.countChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ Math.abs(billSummary.countChange) }}% vs last month
                  </div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Paid</span>
                    <span class="inv-kpi-chip chip-success"><CIcon name="cil-check-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(billSummary.paidAmount) }}</div>
                  <div class="inv-kpi-sub">{{ billSummary.paidCount }} bill{{ billSummary.paidCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Outstanding</span>
                    <span class="inv-kpi-chip chip-warning"><CIcon name="cil-clock" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(billSummary.pendingAmount) }}</div>
                  <div class="inv-kpi-sub">{{ billSummary.pendingCount }} bill{{ billSummary.pendingCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Overdue</span>
                    <span class="inv-kpi-chip chip-danger"><CIcon name="cil-warning" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(billSummary.overdueAmount) }}</div>
                  <div class="inv-kpi-sub">{{ billSummary.overdueCount }} bill{{ billSummary.overdueCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Status Distribution -->
      <CCol lg="4">
        <CCard class="h-100 border-0 shadow-sm">
          <CCardHeader
            class="bg-transparent border-0 d-flex justify-content-between align-items-center p-4"
          >
            <h5 class="mb-0">Status distribution</h5>
          </CCardHeader>
          <CCardBody
            class="pt-0 pb-4 px-4 d-flex flex-column align-items-center justify-content-center"
          >
            <div class="w-100">
              <div
                v-for="status in statusDistribution"
                :key="status.name"
                class="d-flex align-items-center mb-2"
              >
                <div
                  class="mr-2 status-dot"
                  :class="'bg-' + distColor(status.name)"
                ></div>
                <div class="flex-grow-1">{{ distLabel(status.name) }}</div>
                <div class="text-right">
                  <strong>{{ status.count }}</strong>
                  <span class="text-muted ml-1"
                    >({{ status.percentage }}%)</span
                  >
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
        </CTab>

        <!-- Tab 2: All Bills -->
        <CTab>
          <template slot="title">
            <CIcon name="cil-list" class="mr-1" /> All bills
            <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
          </template>

    <!-- Bill List -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardHeader class="bg-transparent border-0 p-4">
        <CRow class="align-items-center">
          <CCol>
            <h5 class="mb-0">
              Bills
              <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
            </h5>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Advanced Filters -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <div class="form-group">
                    <label>Vendor</label>
                    <v-select
                      v-model="selectedVendorFilter"
                      :options="vendorOptions"
                      label="name"
                      placeholder="Select vendor"
                      :clearable="true"
                      @input="onVendorFilterChange"
                    >
                      <template v-slot:option="option">
                        <div>
                          <div class="font-weight-bold">{{ option.name }}</div>
                          <small v-if="option.email" class="text-muted">{{ option.email }}</small>
                        </div>
                      </template>
                      <template v-slot:selected-option="option">
                        <span>{{ option.name }}</span>
                      </template>
                    </v-select>
                  </div>
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Status"
                    v-model="filters.status"
                    @change="applyFilters"
                  >
                    <option value="">All statuses</option>
                    <option
                      v-for="status in statusOptions"
                      :key="status.id"
                      :value="status.name"
                    >
                      {{ badgeLabel('bill', status.name) }}
                    </option>
                  </CSelect>
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Payment status"
                    v-model="filters.isPaid"
                    @change="applyFilters"
                  >
                    <option value="">All</option>
                    <option value="true">Paid</option>
                    <option value="false">Unpaid</option>
                  </CSelect>
                </CCol>
                <CCol md="3">
                  <CInput
                    type="date"
                    label="From"
                    v-model="filters.dateFrom"
                    class="mb-3"
                    @change="applyFilters"
                  />
                  <CInput
                    type="date"
                    label="To"
                    v-model="filters.dateTo"
                    @change="applyFilters"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" class="mr-2" @click="resetFilters">
                    <CIcon name="cil-x" /> Clear filters
                  </CButton>
                  <CButton color="primary" @click="applyFilters">
                    <CIcon name="cil-filter" /> Apply filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Filter Toggle -->
        <CRow class="mb-4 align-items-center">
          <CCol xs="12" class="mb-3 mb-md-0">
            <CButtonGroup>
              <CButton color="light" @click="showFilters = !showFilters">
                <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
                {{ showFilters ? "Hide filters" : "Show filters" }}
              </CButton>
              <CBadge
                v-if="hasActiveFilters"
                color="primary"
                class="ml-2 align-self-center"
              >
                {{ activeFiltersCount }} active
              </CBadge>
            </CButtonGroup>
          </CCol>
        </CRow>

        <!-- Filtered Results Summary -->
        <CCard v-if="!loading && !loadError && computedItems.length > 0" class="mb-3 inv-summary-card">
          <CCardBody class="p-3">
            <div class="d-flex align-items-center mb-3">
              <CIcon name="cil-calculator" class="mr-2 text-brand-navy" />
              <span class="inv-summary-title">Filtered results summary</span>
              <CBadge class="ml-2 badge-soft">{{ filteredSummary.totalCount }} bill{{ filteredSummary.totalCount !== 1 ? 's' : '' }}</CBadge>
            </div>
            <CRow>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Before tax</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.subTotal) }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Tax (SST)</div><div class="inv-stat-val text-warn">{{ formatCurrency(filteredSummary.taxAmount) }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Grand total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Paid</div><div class="inv-stat-val text-success-strong">{{ formatCurrency(filteredSummary.paidAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.paidCount }} bill{{ filteredSummary.paidCount !== 1 ? 's' : '' }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Unpaid</div><div class="inv-stat-val text-danger-strong">{{ formatCurrency(filteredSummary.unpaidAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.unpaidCount }} bill{{ filteredSummary.unpaidCount !== 1 ? 's' : '' }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div><div class="inv-stat-meta">per bill</div></div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Desktop View with Enhanced Data Table -->
        <div>
          <div v-if="loading && !items.length" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading…</span>
            </div>
            <p class="mt-3 text-muted">Loading bills…</p>
          </div>

          <div v-else-if="loadError" class="text-center py-5">
            <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
            <h5>Couldn't load bills</h5>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </div>

          <CDataTable
            v-else
            :items="computedItems"
            :fields="fields"
            :loading="loading"
            :pagination="{
              align: 'end',
              doubleArrows: true
            }"
            :sorter="{ external: true }"
            :table-filter="false"
            :items-per-page-select="{
              values: [10, 25, 50, 100],
              label: 'Items per page:'
            }"
            :items-per-page="perPage"
            :active-page="currentPage"
            :pages="totalPages"
            hover
            striped
            bordered
            responsive
            @pagination-change="onPaginationChange"
            @items-per-page-change="onItemsPerPageChange"
            @sorter-value-change="onSorterChange"
          >
            <!-- Index Column -->
            <template #show_index="{ index }">
              <td class="py-2 text-muted">
                {{ (currentPage - 1) * perPage + index + 1 }}
              </td>
            </template>

            <!-- Vendor Column -->
            <template #vendorName="{ item }">
              <td class="py-2">
                <div class="font-weight-medium cursor-pointer" @click="onEdit(item)">{{ item.vendorName }}</div>
              </td>
            </template>

            <!-- Date Column with Better Formatting -->
            <template #displayDate="{ item }">
              <td class="py-2">
                <div>{{ item.displayDate }}</div>
              </td>
            </template>

            <!-- Amount Column -->
            <template #totalAmount="{ item }">
              <td class="py-2 text-right">
                <div class="font-weight-bold text-dark">
                  {{ formatCurrency(item.totalAmount) }}
                </div>
              </td>
            </template>

            <!-- Status Column -->
            <template #show_status="{ item }">
              <td class="py-2">
                <CBadge
                  :color="badgeColor('bill', item.stateDescription)"
                  class="px-2 py-1"
                >
                  {{ badgeLabel('bill', item.stateDescription) }}
                </CBadge>
              </td>
            </template>

            <!-- Payment Status Column -->
            <template #show_payment_status="{ item }">
              <td class="py-2">
                <div v-if="paymentKey(item)">
                  <CBadge :color="badgeColor('documentPayment', paymentKey(item))" class="px-2 py-1 mb-1">
                    {{ badgeLabel('documentPayment', paymentKey(item)) }}
                  </CBadge>
                  <div v-if="paymentKey(item) === 'Paid'" class="small text-muted">
                    RM {{ formatMyr(getTotalPaid(item)) }}
                  </div>
                  <div v-else-if="getTotalPaid(item) > 0" class="small text-muted">
                    RM {{ formatMyr(getTotalPaid(item)) }} of RM {{ formatMyr(item.totalAmount) }}
                  </div>
                </div>

                <!-- Draft or other status -->
                <div v-else>
                  <span class="text-muted">—</span>
                </div>
              </td>
            </template>

            <!-- Actions Column -->
            <template #show_details="{ item }">
              <td class="py-2 position-relative">
                <div class="text-right action-buttons">
                  <CButtonGroup>
                    <CButton
                      color="light"
                      size="sm"
                      @click="onEdit(item)"
                      title="Edit"
                      aria-label="Edit bill"
                      class="border"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CDropdown
                      size="sm"
                      color="light"
                      placement="bottom-end"
                      class="dropdown-with-zindex border"
                      aria-label="More actions"
                    >
                      <template #toggler-content>
                        <CIcon name="cil-options" />
                        <span class="sr-only">More actions</span>
                      </template>
                      <CDropdownItem @click="onEdit(item)">
                        <CIcon name="cil-pencil" class="mr-2" /> Edit
                      </CDropdownItem>
                      <CDropdownDivider />
                      <CDropdownItem
                        @click="showDeleteConfirmation(item)"
                        class="text-danger"
                      >
                        <CIcon name="cil-trash" class="mr-2" /> Delete
                      </CDropdownItem>
                    </CDropdown>
                  </CButtonGroup>
                </div>
              </td>
            </template>

            <!-- Empty states: filtered vs first use -->
            <template #no-items-view>
              <div v-if="hasActiveFilters" class="text-center py-5">
                <CIcon name="cil-filter-x" size="2xl" class="mb-3 text-muted" />
                <h5>No bills match your filters</h5>
                <CButton color="light" class="mt-2" @click="resetFilters">
                  <CIcon name="cil-x" /> Clear filters
                </CButton>
              </div>
              <div v-else class="text-center py-5">
                <CIcon name="cil-inbox" size="2xl" class="mb-3 text-muted" />
                <h5>No bills yet</h5>
                <p class="text-muted mb-3">
                  Record what you owe your vendors and track what you've paid.
                </p>
                <CButton color="primary" @click="addNew">
                  <CIcon name="cil-plus" /> Create bill
                </CButton>
              </div>
            </template>
          </CDataTable>

          <!-- Pagination Display -->
          <CRow class="mt-2" v-if="!loading && totalItems > 0">
            <CCol md="6" class="text-muted text-center">
              <small>
                Total: {{ totalItems }} bills |
                Pages: {{ totalPages }} |
                Current: {{ currentPage }} |
                Per Page: {{ perPage }}
                <span v-if="hasActiveFilters">(filtered)</span>
              </small>
            </CCol>
            <CCol md="6" class="text-center">
              <CPagination
                v-if="totalPages > 1"
                :activePage="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
                size="sm"
                class="mb-0"
              />
            </CCol>
          </CRow>
        </div>
      </CCardBody>
    </CCard>
        </CTab>
      </CTabs>
    </div>

    <!-- Mobile View: Collapsible Analytics + Bill List -->
    <div class="d-lg-none">
      <!-- Collapsible Analytics Card -->
      <CCard class="mb-3 border-0 shadow-sm">
        <CCardHeader
          role="button"
          tabindex="0"
          :aria-expanded="mobileAnalyticsExpanded ? 'true' : 'false'"
          @click="toggleMobileAnalytics"
          @keydown.enter.prevent="toggleMobileAnalytics"
          @keydown.space.prevent="toggleMobileAnalytics"
          class="cursor-pointer bg-light p-3"
          style="user-select: none;"
        >
          <CRow class="align-items-center">
            <CCol>
              <small class="text-muted d-block mb-2">Quick stats</small>
              <div class="d-flex justify-content-between small">
                <span><strong>{{ billSummary.totalCount }}</strong> <small class="text-muted">Total</small></span>
                <span><strong>{{ formatCurrency(billSummary.paidAmount) }}</strong> <small class="text-muted">Paid</small></span>
                <span><strong>{{ formatCurrency(billSummary.pendingAmount) }}</strong> <small class="text-muted">Unpaid</small></span>
              </div>
            </CCol>
            <CCol xs="auto">
              <CIcon
                :name="mobileAnalyticsExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
                size="lg"
                class="text-primary"
              />
            </CCol>
          </CRow>
        </CCardHeader>
        <CCollapse :show="mobileAnalyticsExpanded">
          <CCardBody class="p-3">
            <!-- Compact Summary Metrics -->
            <CRow class="mb-3">
              <CCol xs="6" class="mb-2">
                <CCallout color="info" class="mb-0 p-2">
                  <div class="text-muted small">Total bills</div>
                  <div class="font-weight-bold h5 mb-0">{{ billSummary.totalCount }}</div>
                </CCallout>
              </CCol>
              <CCol xs="6" class="mb-2">
                <CCallout color="success" class="mb-0 p-2">
                  <div class="text-muted small">Paid</div>
                  <div class="font-weight-bold h5 mb-0">{{ formatCurrency(billSummary.paidAmount) }}</div>
                  <small class="text-success">{{ billSummary.paidCount }} bills</small>
                </CCallout>
              </CCol>
              <CCol xs="6" class="mb-2">
                <CCallout color="warning" class="mb-0 p-2">
                  <div class="text-muted small">Unpaid</div>
                  <div class="font-weight-bold h5 mb-0">{{ formatCurrency(billSummary.pendingAmount) }}</div>
                  <small class="text-muted">{{ billSummary.pendingCount }} bills</small>
                </CCallout>
              </CCol>
              <CCol xs="6" class="mb-2">
                <CCallout color="danger" class="mb-0 p-2">
                  <div class="text-muted small">Overdue</div>
                  <div class="font-weight-bold h5 mb-0">{{ formatCurrency(billSummary.overdueAmount) }}</div>
                  <small class="text-danger">{{ billSummary.overdueCount}} bills</small>
                </CCallout>
              </CCol>
            </CRow>

            <!-- Mobile Status Distribution -->
            <div class="text-center">
              <h6 class="mb-3">Status distribution</h6>
              <div class="mt-3">
                <div
                  v-for="status in statusDistribution"
                  :key="status.name"
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <div class="d-flex align-items-center">
                    <div
                      class="mr-2 status-dot"
                      :class="'bg-' + distColor(status.name)"
                    ></div>
                    <span>{{ distLabel(status.name) }}</span>
                  </div>
                  <div>
                    <strong>{{ status.count }}</strong>
                    <span class="text-muted ml-1">({{ status.percentage }}%)</span>
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCollapse>
      </CCard>

      <!-- Mobile Bill List (uses existing mobile cards from original code) -->
      <CCard class="mb-4 border-0 shadow-sm">
        <CCardHeader class="bg-transparent border-0 p-3">
          <CRow class="align-items-center">
            <CCol>
              <h5 class="mb-0">
                Bills
                <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
              </h5>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody class="p-3">
          <!-- Filters Toggle -->
          <CRow class="mb-3 align-items-center">
            <CCol xs="12">
              <CButton
                color="light"
                block
                @click="showFilters = !showFilters"
                class="d-flex align-items-center justify-content-between"
              >
                <span>
                  <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
                  {{ showFilters ? "Hide filters" : "Show filters" }}
                </span>
                <CBadge v-if="hasActiveFilters" color="primary">
                  {{ activeFiltersCount }}
                </CBadge>
              </CButton>
            </CCol>
          </CRow>

          <!-- Mobile Filters (reuse existing filter section) -->
          <CCollapse :show="showFilters">
            <CCard class="mb-3 border">
              <CCardBody class="p-2">
                <CRow>
                  <CCol xs="12" class="mb-2">
                    <div class="form-group mb-0">
                      <label class="small">Vendor</label>
                      <v-select
                        v-model="selectedVendorFilter"
                        :options="vendorOptions"
                        label="name"
                        placeholder="Select vendor"
                        :clearable="true"
                        @input="onVendorFilterChange"
                      >
                        <template v-slot:option="option">
                          <div>
                            <div class="font-weight-bold">{{ option.name }}</div>
                            <small v-if="option.email" class="text-muted">{{ option.email }}</small>
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CSelect
                      label="Status"
                      v-model="filters.status"
                      @change="applyFilters"
                      size="sm"
                    >
                      <option value="">All</option>
                      <option
                        v-for="status in statusOptions"
                        :key="status.id"
                        :value="status.name"
                      >
                        {{ badgeLabel('bill', status.name) }}
                      </option>
                    </CSelect>
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CSelect
                      label="Payment"
                      v-model="filters.isPaid"
                      @change="applyFilters"
                      size="sm"
                    >
                      <option value="">All</option>
                      <option value="true">Paid</option>
                      <option value="false">Unpaid</option>
                    </CSelect>
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CInput
                      type="date"
                      label="From"
                      v-model="filters.dateFrom"
                      @change="applyFilters"
                      size="sm"
                    />
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CInput
                      type="date"
                      label="To"
                      v-model="filters.dateTo"
                      @change="applyFilters"
                      size="sm"
                    />
                  </CCol>
                </CRow>
                <CRow class="mt-2">
                  <CCol xs="6">
                    <CButton color="light" size="sm" block @click="resetFilters">
                      <CIcon name="cil-x" size="sm" /> Clear
                    </CButton>
                  </CCol>
                  <CCol xs="6">
                    <CButton color="primary" size="sm" block @click="applyFilters">
                      <CIcon name="cil-filter" size="sm" /> Apply
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCollapse>

          <!-- Mobile Filtered Summary -->
          <CCard v-if="!loading && !loadError && computedItems.length > 0" class="mb-3 inv-summary-card">
            <CCardBody class="p-2">
              <div class="d-flex align-items-center mb-2">
                <CIcon name="cil-calculator" size="sm" class="mr-1 text-brand-navy" />
                <span class="inv-summary-title">Summary</span>
                <CBadge class="ml-1 badge-soft">{{ filteredSummary.totalCount }}</CBadge>
              </div>
              <CRow class="text-center">
                <CCol xs="4" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Before tax</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.subTotal) }}</div></div></CCol>
                <CCol xs="4" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Tax</div><div class="inv-stat-val text-warn">{{ formatCurrency(filteredSummary.taxAmount) }}</div></div></CCol>
                <CCol xs="4" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div></CCol>
                <CCol xs="4"><div class="inv-stat-mini"><div class="inv-stat-label">Paid</div><div class="inv-stat-val text-success-strong">{{ formatCurrency(filteredSummary.paidAmount) }}</div></div></CCol>
                <CCol xs="4"><div class="inv-stat-mini"><div class="inv-stat-label">Unpaid</div><div class="inv-stat-val text-danger-strong">{{ formatCurrency(filteredSummary.unpaidAmount) }}</div></div></CCol>
                <CCol xs="4"><div class="inv-stat-mini"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div></div></CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Mobile Loading State -->
          <div v-if="loading && !items.length" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading…</span>
            </div>
            <p class="mt-3 text-muted">Loading bills…</p>
          </div>

          <!-- Mobile Error State -->
          <div v-else-if="loadError" class="text-center py-5">
            <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
            <h5>Couldn't load bills</h5>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </div>

          <!-- Mobile Empty State (filtered) -->
          <div v-else-if="computedItems.length === 0 && hasActiveFilters" class="text-center py-5">
            <CIcon name="cil-filter-x" size="2xl" class="text-muted mb-3" />
            <h5>No bills match your filters</h5>
            <CButton color="light" class="mt-2" @click="resetFilters">
              <CIcon name="cil-x" /> Clear filters
            </CButton>
          </div>

          <!-- Mobile Empty State (first use) -->
          <div v-else-if="computedItems.length === 0" class="text-center py-5">
            <CIcon name="cil-inbox" size="2xl" class="text-muted mb-3" />
            <h5>No bills yet</h5>
            <p class="text-muted mb-3">
              Record what you owe your vendors and track what you've paid.
            </p>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create bill
            </CButton>
          </div>

          <!-- Mobile Bill Cards -->
          <div v-else>
            <!-- Items per page selector -->
            <CRow class="mb-3">
              <CCol xs="6">
                <CSelect v-model="perPage" @change="onItemsPerPageChange" size="sm" aria-label="Bills per page">
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                </CSelect>
              </CCol>
              <CCol xs="6" class="text-right text-muted d-flex align-items-center justify-content-end small">
                {{ paginationStart }}-{{ paginationEnd }} of {{ totalItems }}
              </CCol>
            </CRow>

            <!-- Bill Cards -->
            <CCard
              v-for="item in computedItems"
              :key="item.id"
              class="mb-3 border-0 shadow-sm"
            >
              <CCardHeader class="d-flex justify-content-between align-items-center py-2 bg-light">
                <div>
                  <strong>{{ item.vendorName }}</strong>
                </div>
                <CBadge :color="badgeColor('bill', item.stateDescription)">
                  {{ badgeLabel('bill', item.stateDescription) }}
                </CBadge>
              </CCardHeader>
              <CCardBody class="py-2">
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Date</div>
                  <div class="small">{{ item.displayDate }}</div>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Amount</div>
                  <div class="font-weight-bold small text-right">
                    {{ formatCurrency(item.totalAmount) }}
                  </div>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Payment</div>
                  <div>
                    <CBadge v-if="paymentKey(item)" :color="badgeColor('documentPayment', paymentKey(item))" class="px-2 py-1">
                      {{ badgeLabel('documentPayment', paymentKey(item)) }}
                    </CBadge>
                    <span v-else class="text-muted">—</span>
                  </div>
                </div>
                <div v-if="getTotalPaid(item) > 0 && paymentKey(item) !== 'Paid'" class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Paid so far</div>
                  <div class="small text-right">
                    {{ formatCurrency(getTotalPaid(item)) }} of {{ formatCurrency(item.totalAmount) }}
                  </div>
                </div>
                <div v-if="item.isPaid && item.paymentDate" class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Paid on</div>
                  <div class="small">{{ getDisplayDate(item.paymentDate) }}</div>
                </div>
                <div v-if="item.isPaid && item.paymentMethod" class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Method</div>
                  <div class="small">{{ item.paymentMethod }}</div>
                </div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-white">
                <CButton color="light" size="sm" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CDropdown size="sm" color="light" placement="bottom-end">
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem @click="onEdit(item)">Open bill</CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem @click="showDeleteConfirmation(item)" class="text-danger">
                    Delete
                  </CDropdownItem>
                </CDropdown>
              </CCardFooter>
            </CCard>

            <!-- Mobile Pagination -->
            <CRow class="mt-3">
              <CCol xs="12" class="d-flex justify-content-center">
                <CPagination
                  :activePage="currentPage"
                  :pages="totalPages"
                  @update:activePage="onPageChange"
                  size="sm"
                />
              </CCol>
            </CRow>
          </div>
        </CCardBody>
      </CCard>
    </div>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="'Delete the bill from ' + (itemToDelete.vendorName || 'this vendor') + '?'"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-0">
        The {{ formatCurrency(itemToDelete.totalAmount) }} bill<span v-if="itemToDelete.displayDate"> dated {{ itemToDelete.displayDate }}</span>
        and its line items will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? "Deleting…" : "Delete bill" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import BillApi from "@/lib/billApi";
import VendorApi from "@/lib/vendorApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { formatMyr } from "@/utils/money";
import { formatDate, mytToday, asDateString } from "@/utils/datetime";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";

export default {
  name: "BillList",
  components: {
    vSelect,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:50px",
          sorter: false,
          filter: false,
        },
        {
          key: "vendorName",
          label: "Vendor",
          sorter: true,
          filter: true
        },
        {
          key: "displayDate",
          label: "Date",
          sorter: true,
          filter: true
        },
        {
          key: "totalAmount",
          label: "Amount",
          sorter: true,
          filter: false,
          _style: "text-align: right",
          _classes: "text-right"
        },
        {
          key: "show_status",
          label: "Status",
          sorter: true,
          filter: true
        },
        {
          key: "show_payment_status",
          label: "Payment",
          sorter: false,
          filter: false,
          _style: "width:120px"
        },
        {
          key: "show_details",
          label: "Actions",
          _style: "width:140px",
          sorter: false,
          filter: false,
        },
      ],
      details: [],
      collapseDuration: 0,
      api: new BillApi(),
      vendorApi: new VendorApi(),
      warningModal: false,
      itemToDelete: {},

      // Pagination
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      totalPages: 1,

      // Sorting
      sortBy: 'date',
      sortDirection: 'desc',

      // Filters
      showFilters: false,
      filters: {
        vendorId: "",
        status: "",
        isPaid: "",
        dateFrom: "",
        dateTo: "",
      },
      selectedVendorFilter: null,

      // Mobile Analytics
      mobileAnalyticsExpanded: false,

      // Status options from API
      statusOptions: [],

      // Vendor options
      vendorItems: [],

      // Real analytics source — all bills for the org-wide summary
      // (separate one-time pull; the paginated table uses `items`)
      allBills: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Vendor Bills"]);
    this.loadStatusOptions();
    this.loadVendors();
    this.refreshTable();
    this.fetchSummary(); // Load org-wide bills for real analytics
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          displayDate: this.getDisplayDate(item.date),
          vendorName: this.getVendorName(item),
          totalAmount: parseFloat(item.totalAmount) || 0,
        };
      });
    },

    // Pagination computed properties
    paginationStart() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },

    // Filter tracking
    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== "") || this.selectedVendorFilter !== null;
    },
    activeFiltersCount() {
      let count = Object.values(this.filters).filter(filter => filter !== "").length;
      if (this.selectedVendorFilter) count++;
      return count;
    },

    // Vendor options for filter
    vendorOptions() {
      return this.vendorItems;
    },

    // Filtered bill summary for the current view (page-level, real)
    filteredSummary() {
      const bills = this.computedItems;

      let subTotal = 0;
      let taxAmount = 0;
      let totalAmount = 0;
      let paidAmount = 0;
      let unpaidAmount = 0;
      let paidCount = 0;
      let unpaidCount = 0;

      bills.forEach(bill => {
        const breakdown = this.calculateTaxBreakdown(bill);
        const amount = parseFloat(bill.totalAmount) || breakdown.grandTotal || 0;

        subTotal += breakdown.subTotal;
        taxAmount += breakdown.taxAmount;
        totalAmount += amount;

        const paid = this.billIsPaid(bill);
        if (paid) {
          paidAmount += amount;
          paidCount++;
        } else if (bill.stateDescription === 'Approve') {
          unpaidAmount += this.billOutstanding(bill, amount);
          unpaidCount++;
        }
      });

      const averageAmount = bills.length > 0 ? totalAmount / bills.length : 0;

      return {
        subTotal,
        taxAmount,
        totalAmount,
        paidAmount,
        unpaidAmount,
        paidCount,
        unpaidCount,
        averageAmount,
        totalCount: bills.length
      };
    },

    // ===== Real analytics (computed from allBills) =====
    // Org-wide summary; keys preserved so desktop + mobile templates stay unchanged.
    billSummary() {
      const all = this.allBills || [];
      let paidAmount = 0, paidCount = 0, pendingAmount = 0, pendingCount = 0, overdueAmount = 0, overdueCount = 0;
      all.filter((b) => b.stateDescription === "Approve").forEach((b) => {
        const amt = this.billAmount(b);
        if (this.billIsPaid(b)) { paidAmount += amt; paidCount++; }
        else if (this.billOverdue(b)) { overdueAmount += this.billOutstanding(b, amt); overdueCount++; }
        else { pendingAmount += this.billOutstanding(b, amt); pendingCount++; }
      });
      const thisMonth = mytToday().slice(0, 7);
      const [ty, tm] = thisMonth.split("-").map(Number);
      const lastMonth = tm === 1 ? `${ty - 1}-12` : `${ty}-${String(tm - 1).padStart(2, "0")}`;
      const datedIn = (ym) => all.filter((b) => asDateString(b.date).slice(0, 7) === ym).length;
      const cThis = datedIn(thisMonth), cLast = datedIn(lastMonth);
      const countChange = cLast ? Math.round(((cThis - cLast) / cLast) * 1000) / 10 : (cThis > 0 ? 100 : 0);
      return { totalCount: all.length, countChange, paidAmount, paidCount, pendingAmount, pendingCount, overdueAmount, overdueCount };
    },

    statusDistribution() {
      const s = this.billSummary;
      const draftCount = (this.allBills || []).filter((b) => b.stateDescription === "Draft").length;
      const items = [
        { name: "Paid", count: s.paidCount },
        { name: "Unpaid", count: s.pendingCount },
        { name: "Overdue", count: s.overdueCount },
        { name: "Draft", count: draftCount },
      ].filter((x) => x.count > 0);
      const total = items.reduce((a, b) => a + b.count, 0) || 1;
      return items.map((x) => ({ ...x, percentage: Math.round((x.count / total) * 1000) / 10 }));
    },
  },
  methods: {
    // Mobile Analytics Toggle
    toggleMobileAnalytics() {
      this.mobileAnalyticsExpanded = !this.mobileAnalyticsExpanded;
    },

    badgeColor,
    badgeLabel,
    formatMyr,

    getDisplayDate(dt) {
      return formatDate(dt);
    },

    // Status-distribution rows: Draft uses the bill map, the rest the payment map
    distColor(name) {
      return name === "Draft" ? badgeColor("bill", name) : badgeColor("documentPayment", name);
    },
    distLabel(name) {
      return name === "Draft" ? badgeLabel("bill", name) : badgeLabel("documentPayment", name);
    },

    // Payment badge key for an approved bill: Paid | Partial | Overdue | Unpaid (null for drafts)
    paymentKey(bill) {
      const status = this.getPaymentStatus(bill);
      if (status === "paid") return "Paid";
      if (bill.stateDescription !== "Approve") return null;
      if (this.billOverdue(bill)) return "Overdue";
      return status === "partial" ? "Partial" : "Unpaid";
    },

    getVendorName(item) {
      if (item.vendor && item.vendor.name) {
        return item.vendor.name;
      }
      return "N/A";
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },

    /**
     * Calculate payment status based on actual payment amounts
     * @param {Object} bill - Bill object with payments array and totalAmount
     * @returns {String} - 'paid', 'partial', or 'unpaid'
     */
    getPaymentStatus(bill) {
      if (!bill) return 'unpaid';

      const totalAmount = parseFloat(bill.totalAmount) || 0;

      // If bill has no total amount, consider it unpaid
      if (totalAmount === 0) return 'unpaid';

      // Calculate total paid from payments array
      const totalPaid = this.getTotalPaid(bill);

      // Determine status
      if (totalPaid === 0) return 'unpaid';
      if (totalPaid >= totalAmount) return 'paid';
      return 'partial';
    },

    /**
     * Get total paid amount for a bill
     * @param {Object} bill - Bill object with payments array
     * @returns {Number} - Total amount paid
     */
    getTotalPaid(bill) {
      if (!bill || !bill.payments || bill.payments.length === 0) return 0;
      return bill.payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    },

    // ===== Analytics helpers (org-wide summary from allBills) =====
    fetchSummary() {
      this.api
        .getListByCurrentBusiness({ page: 1, pageSize: 1000 })
        .then((r) => {
          // Bills endpoint returns a raw array (in result/data); not always paginated.
          this.allBills = (r && (r.result?.data ?? r.data ?? r.result)) || [];
          if (!Array.isArray(this.allBills)) this.allBills = [];
        })
        .catch(() => { /* analytics degrade gracefully; the table still works */ });
    },

    // A bill's gross amount (server-computed total)
    billAmount(b) {
      return Number(b.totalAmount) || 0;
    },

    // Total paid across the bill's payments
    billPaid(b) {
      return (b.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
    },

    // Whether a bill is fully settled
    billIsPaid(b) {
      if (b.isFullPaid || b.isPaid) return true;
      return this.billOutstanding(b, this.billAmount(b)) <= 0.01;
    },

    // Remaining balance: prefer server-computed amountDue, fall back to amount - totalPaid
    billOutstanding(b, amt) {
      const amount = amt != null ? amt : this.billAmount(b);
      const due = b.amountDue != null ? Number(b.amountDue) : (amount - (Number(b.totalPaid) || this.billPaid(b)));
      return Math.max(0, isNaN(due) ? 0 : due);
    },

    // Past-due if outstanding and due date is before today
    billOverdue(b) {
      return this.billOutstanding(b) > 0.01 && !!b.dueDate && asDateString(b.dueDate) < mytToday();
    },

    // Tax breakdown for a bill's line items (used by the filtered-results summary)
    calculateTaxBreakdown(bill) {
      let subtotal = 0;
      if (bill.items && bill.items.length > 0) {
        for (let i = 0; i < bill.items.length; i++) {
          const item = bill.items[i];
          subtotal += (item.price || 0) * (item.quantity || 0);
        }
      }
      const taxRate = bill.salesTax?.rateInPercentage || bill.taxRateInPercentage || 0;
      const taxAmount = (taxRate * subtotal) / 100;
      const total = Number(bill.totalAmount) || (subtotal + taxAmount);
      // If we have no line items, treat the whole total as before-tax (no SST breakdown available)
      if (subtotal === 0) {
        return { subTotal: parseFloat(total.toFixed(2)), taxAmount: 0, taxPercentage: 0, grandTotal: parseFloat(total.toFixed(2)) };
      }
      return {
        subTotal: parseFloat(subtotal.toFixed(2)),
        taxAmount: parseFloat(taxAmount.toFixed(2)),
        taxPercentage: taxRate,
        grandTotal: parseFloat((subtotal + taxAmount).toFixed(2)),
      };
    },

    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;

      // Build params for API call
      const params = {
        page: self.currentPage,
        pageSize: self.perPage,
        sortBy: self.sortBy,
        sortDirection: self.sortDirection
      };

      // Add filters
      if (self.filters.vendorId) {
        params.vendorId = self.filters.vendorId;
      }
      if (self.filters.status) {
        params.status = self.filters.status;
      }
      if (self.filters.isPaid !== "") {
        params.isPaid = self.filters.isPaid;
      }
      if (self.filters.dateFrom) {
        params.dateFrom = self.filters.dateFrom;
      }
      if (self.filters.dateTo) {
        params.dateTo = self.filters.dateTo;
      }

      self.api
        .getListByCurrentBusiness(params)
        .then((response) => {
          // Handle paginated API response
          if (response && response.data) {
            self.items = response.data || [];
            self.totalItems = response.totalRecords || 0;
            self.currentPage = response.page || 1;
            self.totalPages = response.totalPages || 1;
          } else if (response.result && response.result.data) {
            self.items = response.result.data || [];
            self.totalItems = response.result.totalRecords || 0;
            self.currentPage = response.result.page || 1;
            self.totalPages = response.result.totalPages || 1;
          } else {
            // Simple response (backward compatibility)
            self.items = response.result || response || [];
            self.totalItems = self.items.length;
            self.totalPages = Math.ceil(self.totalItems / self.perPage);
          }

          // Ensure totalPages is at least 1 if there are items
          if (self.totalItems > 0 && self.totalPages < 1) {
            self.totalPages = 1;
          }

          self.loading = false;
        })
        .catch((error) => {
          console.error("Failed to load bills:", error);
          self.loadError = true;
          self.items = [];
          self.totalItems = 0;
          self.totalPages = 1;
          self.loading = false;
        });
    },

    onEdit(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/Bill/${item.id}`,
      });
    },

    showDeleteConfirmation(item) {
      var self = this;
      self.itemToDelete = item;
      self.warningModal = true;
    },

    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.refreshTable();
          this.fetchSummary();
          this.$toast.success("Bill deleted");
          this.warningModal = false;
        })
        .catch((err) => {
          this.$toast.error(
            "Couldn't delete the bill. " + helper.getErrorMessage(err && err.data, "Please try again.")
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    onDeleteConfirmation(status, evt, accept) {
      if (!status) {
        this.itemToDelete = {};
      }
    },

    addNew() {
      this.$router.push({ path: "/tenants/Bill" });
    },

    // Pagination event handlers
    onPaginationChange(paginationData) {
      if (paginationData !== this.perPage) {
        this.perPage = parseInt(paginationData);
        this.currentPage = 1;
        this.refreshTable();
      }
    },

    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.refreshTable();
      }
    },

    onItemsPerPageChange(itemsPerPage) {
      if (itemsPerPage !== this.perPage) {
        this.perPage = parseInt(itemsPerPage);
        this.currentPage = 1;
        this.refreshTable();
      }
    },

    // Sorting event handler
    onSorterChange(sorter) {
      if (sorter && sorter.column) {
        this.sortBy = sorter.column;
        this.sortDirection = sorter.asc ? 'asc' : 'desc';
        this.currentPage = 1;
        this.refreshTable();
      }
    },

    // Filter methods
    resetFilters() {
      this.filters = {
        vendorId: "",
        status: "",
        isPaid: "",
        dateFrom: "",
        dateTo: "",
      };
      this.selectedVendorFilter = null;
      this.currentPage = 1;
      this.refreshTable();
    },

    applyFilters() {
      this.currentPage = 1;
      this.refreshTable();
      // Don't auto-hide filters on mobile for better UX
      if (window.innerWidth >= 768) {
        this.showFilters = false;
      }
    },

    // Load status options from API
    loadStatusOptions() {
      var self = this;
      // Use static options for bills (matching the existing getBadge function)
      self.statusOptions = [
        { id: 0, name: "Draft" },
        { id: 1, name: "Approve" }
      ];
    },

    // Load vendors for filter
    loadVendors() {
      var self = this;
      self.vendorApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.vendorItems = response.result || [];
        })
        .catch((error) => {
          console.error("Error loading vendors:", error);
          self.vendorItems = [];
        });
    },

    // Handle vendor filter selection
    onVendorFilterChange(vendor) {
      if (vendor) {
        this.filters.vendorId = vendor.id;
      } else {
        this.filters.vendorId = "";
      }
      this.applyFilters();
    },
  },
};
</script>

<style scoped>
/* Fix dropdown z-index issues */
.dropdown-with-zindex {
  position: static;
}

.dropdown-with-zindex .dropdown-menu {
  z-index: 1050 !important;
  position: absolute;
  will-change: transform;
}

.dropdown-mobile .dropdown-menu {
  z-index: 1055 !important;
  position: absolute;
  will-change: transform;
}

/* Enhanced action buttons */
.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

/* Lift the open row above its neighbours so the dropdown menu always
   renders on top instead of behind the rows below it. */
.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}

.action-buttons .dropdown-toggle::after {
  margin-left: 0.5em;
}

/* Clickable elements */
.cursor-pointer {
  cursor: pointer;
  transition: color 0.15s ease-in-out;
}

.cursor-pointer:hover {
  text-decoration: underline;
  color: #1e3a5f !important;
}

/* Loading spinner center alignment */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* Badge improvements */
.badge {
  font-size: 0.75em;
  font-weight: 500;
  letter-spacing: 0.025em;
}

/* Better spacing for mobile pagination */
.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* Improve mobile responsiveness */
@media (max-width: 575.98px) {
  .btn-group {
    flex-wrap: wrap;
  }

  .btn-group .btn {
    margin-bottom: 0.25rem;
  }
}

/* Custom styling for mobile cards */
@media (max-width: 991.98px) {
  .d-none.d-lg-block {
    display: none !important;
  }
}

@media (min-width: 992px) {
  .d-lg-none {
    display: none !important;
  }
}

/* Status distribution dot */
.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

/* Card hover effects for mobile */
@media (max-width: 991.98px) {
  .card {
    border: 1px solid #dee2e6;
  }

}

/* Improve dropdown menu appearance */
.dropdown-menu {
  border: 1px solid #dee2e6;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  border-radius: 0.375rem;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
}

/* Button group improvements */
.btn-group .btn {
  border-color: #dee2e6;
}

.btn-group .btn:hover {
  border-color: #adb5bd;
  z-index: 1;
}

/* v-select styling for filters */
.v-select {
  width: 100%;
}

.v-select .vs__dropdown-toggle {
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  min-height: 38px;
}

.v-select.vs--open .vs__dropdown-toggle {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.v-select .vs__search {
  margin: 0;
  padding: 4px 0;
}

.v-select .vs__selected {
  margin: 0;
  padding: 0;
}

.v-select .vs__dropdown-menu {
  border: 1px solid #d8dbe0;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  border-radius: 0.25rem;
  max-height: 250px;
}

.v-select .vs__dropdown-option {
  padding: 8px 12px;
}

.v-select .vs__dropdown-option--highlight {
  background-color: #f8f9fa;
  color: #16181b;
}

.v-select .vs__dropdown-option--selected {
  background-color: #007bff;
  color: #fff;
}

.v-select .vs__clear {
  margin-right: 4px;
}

/* Cursor pointer for clickable elements */
.cursor-pointer {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.cursor-pointer:hover {
  background-color: #f8f9fa !important;
}

.cursor-pointer:active {
  background-color: #e9ecef !important;
}

/* Mobile-specific optimizations */
@media (max-width: 991.98px) {
  /* Improve touch targets */
  .btn-sm {
    min-height: 38px;
    padding: 0.5rem 0.75rem;
  }

  /* Compact callouts for mobile */
  .c-callout {
    padding: 0.75rem !important;
  }

  /* Better spacing for mobile cards */
  .card-body {
    padding: 1rem !important;
  }

  /* Ensure tabs are hidden on mobile */
  .nav-tabs {
    display: none;
  }

  /* Improved quick stats readability */
  .quick-stats-mobile {
    font-size: 0.85rem;
  }
}

/* Tab styling improvements */
.nav-tabs .nav-link {
  font-weight: 600;
  color: #64748b;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.75rem 1.5rem;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.nav-tabs .nav-link.active {
  color: #1e3a5f;
  border-bottom-color: #10b981;
  background-color: transparent;
}

.nav-tabs .nav-link:hover {
  color: #1e3a5f;
  border-bottom-color: #10b981;
}

.nav-tabs {
  border-bottom: 1px solid #d8dbe0;
  margin-bottom: 1.5rem;
}

/* Badge in tab improvements */
.nav-link .badge {
  margin-left: 0.5rem;
  font-size: 0.7em;
  font-weight: 600;
  padding: 0.25em 0.6em;
}

/* Mobile analytics collapse — height handled by CCollapse (avoid layout-thrash transition) */

/* Sticky analytics header on mobile (optional enhancement) */
@media (max-width: 991.98px) {
  .mobile-analytics-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
  }
}

/* ============================================================= */
/* Brand redesign — navy #1E3A5F / emerald #10B981               */
/* ============================================================= */
h1 { color: #1e3a5f; letter-spacing: -0.01em; }

/* Buttons → brand */
::v-deep .btn-primary {
  background-color: #10b981;
  border-color: #10b981;
}
::v-deep .btn-primary:hover,
::v-deep .btn-primary:focus,
::v-deep .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #059669;
  border-color: #059669;
}
::v-deep .btn-outline-primary {
  color: #1e3a5f;
  border-color: #1e3a5f;
}
::v-deep .btn-outline-primary:hover,
::v-deep .btn-outline-primary.active,
::v-deep .btn-outline-primary:not(:disabled):not(.disabled).active {
  background-color: #1e3a5f;
  border-color: #1e3a5f;
  color: #fff;
}
::v-deep .badge-success { background-color: #10b981; }

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.72rem; font-weight: 600; letter-spacing: 0.05em; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: #ef4444; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; letter-spacing: -0.02em; }
.inv-kpi-sub { font-size: 0.74rem; color: #94a3b8; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

/* Filtered Results Summary */
.inv-summary-card { border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: none; }
.inv-summary-title { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.04em; color: #1e3a5f; }
.badge-soft { background: #eef2f7; color: #1e3a5f; font-weight: 600; }
.inv-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 0.6rem 0.7rem; height: 100%; }
.inv-stat-mini { padding: 0.15rem; }
.inv-stat-label { font-size: 0.68rem; letter-spacing: 0.03em; color: #94a3b8; }
.inv-stat-val { font-size: 0.95rem; font-weight: 700; color: #334155; }
.inv-stat-meta { font-size: 0.66rem; color: #94a3b8; }
.text-brand-navy { color: #1e3a5f !important; }
.text-success-strong { color: #059669 !important; }
.text-danger-strong { color: #ef4444 !important; }
.text-warn { color: #b45309 !important; }

/* Table polish */
::v-deep .table thead th {
  font-size: 0.72rem;
 
  letter-spacing: 0.03em;
  color: #64748b;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}
::v-deep .table tbody td { vertical-align: middle; }
::v-deep .table.table-hover tbody tr:hover { background: #f8fafc; }
</style>
